import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowLeft, Calendar, Mail } from 'lucide-react';
import { documentarySections, docBlurb, droneShowcase, contactInfo } from '../mock';
import VideoModal from '../components/VideoModal';
import LogoTicker from '../components/LogoTicker';
import Seo from '../components/Seo';
import { breadcrumb } from '../lib/seoSchemas';

const thumbOf = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

const DocCard = ({ d, onPlay }) => (
  <button
    onClick={() => onPlay(d.youtubeId, d.title)}
    data-testid={`doc-card-${d.id}`}
    className="group relative rounded-2xl overflow-hidden text-left border border-white/10 hover:border-[#c9a85f]/60 transition-all aspect-video"
  >
    <img src={thumbOf(d.youtubeId)} alt={d.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#c9a85f]/90 text-black group-hover:scale-110 transition-transform shadow-xl">
        <Play size={22} className="fill-black ml-1" />
      </span>
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-5">
      <h3 className="font-display font-semibold text-white text-base md:text-lg leading-tight uppercase group-hover:text-[#c9a85f] transition-colors">{d.title}</h3>
    </div>
  </button>
);

export default function Documentaries() {
  const [video, setVideo] = useState(null);
  const play = useCallback((youtubeId, title) => setVideo({ youtubeId, title }), []);
  const close = useCallback(() => setVideo(null), []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative">
      <Seo
        title="Documentary & Aerial Video Production | Simply Gold Productions"
        description="Behind-the-scenes documentary filmmaking and cinematic drone & aerial work by Simply Gold Productions — authentic storytelling that connects with audiences across Canada."
        path="/documentaries"
        type="video.other"
        jsonLd={breadcrumb('Documentaries', '/documentaries')}
      />
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="doc-logo-home">
            <img src="/sgp-logo-header.png" alt="Simply Gold Productions logo" width="1222" height="629" className="h-12 md:h-14 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="doc-back-link" className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-[#c9a85f] text-sm font-medium transition-colors">
              <ArrowLeft size={16} /> Back to site
            </Link>
            <a href={`mailto:${contactInfo.email}`} data-testid="doc-contact-btn" className="inline-flex items-center gap-2 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold text-sm px-5 py-2.5 rounded-md transition-colors tracking-wide">
              <Mail size={15} /> Contact
            </a>
          </div>
        </div>
      </header>

      <main>
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-14 md:pt-28 md:pb-16 text-center">
        <p className="font-display tracking-[0.4em] text-[#c9a85f] text-sm uppercase mb-6">Documentary &amp; Storytelling</p>
        <h1 className="font-display font-bold text-white text-5xl md:text-7xl uppercase leading-[0.95]">Documentaries</h1>
        <p className="mt-7 text-white/60 text-base md:text-lg max-w-2xl mx-auto font-light">{docBlurb}</p>
      </section>

      {documentarySections.map((section) => (
        <section key={section.id} className="max-w-7xl mx-auto px-6 pb-6 md:pb-10" data-testid={`doc-section-${section.id}`}>
          <div className="flex items-center gap-4 mb-8">
            <h2 className="font-display font-bold text-white text-2xl md:text-3xl uppercase tracking-wide whitespace-nowrap">{section.label}</h2>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {section.videos.map((d) => (
              <DocCard key={d.id} d={d} onPlay={play} />
            ))}
          </div>
        </section>
      ))}

      <section className="max-w-7xl mx-auto px-6 pb-6 md:pb-10" data-testid="doc-section-aerial">
        <div className="flex items-center gap-4 mb-8">
          <h2 className="font-display font-bold text-white text-2xl md:text-3xl uppercase tracking-wide whitespace-nowrap">Aerial &amp; Drone</h2>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[{ id: 'drone-reel', title: droneShowcase.reel.title, youtubeId: droneShowcase.reel.youtubeId }, ...droneShowcase.clips].map((d) => (
            <DocCard key={d.id} d={d} onPlay={play} />
          ))}
        </div>
      </section>

      <div className="pt-10">
        <LogoTicker category="Documentary" heading="Documentary Subjects & Partners" />
      </div>

      <section className="border-t border-white/10 bg-[#0c0c0c]">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display font-bold text-white text-3xl md:text-4xl uppercase">Have a story worth telling?</h2>
          <p className="text-white/55 mt-4 max-w-xl mx-auto">Let's create a documentary that connects with your audience and leaves a lasting impression.</p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={contactInfo.calendly} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold px-7 py-3.5 rounded-md transition-colors tracking-wide">
              <Calendar size={18} /> Book A Meeting
            </a>
            <a href={`mailto:${contactInfo.email}`} className="inline-flex items-center gap-2 text-white/85 hover:text-[#c9a85f] font-display font-medium px-4 py-3.5 tracking-wide transition-colors">
              <Mail size={18} /> {contactInfo.email}
            </a>
          </div>
        </div>
      </section>
      </main>


      <footer className="py-8 text-center text-white/40 text-xs border-t border-white/10">
        © {new Date().getFullYear()} {contactInfo.company} — {contactInfo.tagline}
      </footer>

      {video && <VideoModal youtubeId={video.youtubeId} title={video.title} onClose={close} />}
    </div>
  );
}
